/**
 * Invoice & Payment Engine — Phase 1 + Phase 2 Integration
 * Deterministic, idempotent invoice generation and payment recording.
 * Integrates with canonical readiness model and financial clearance engine.
 * ADDITIVE ONLY — does not modify existing financial fields.
 */

import type { Job, Invoice, InvoicePlan, Payment, InvoiceType, InvoiceStatus, PaymentMethod } from "./types";
import { deriveReadinessFromJobExcludingBuildReview } from "./readiness";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";

// ── Derived Payment Status ──

export type DerivedPaymentStatus =
  | "no_invoice_plan"
  | "deposit_pending"
  | "deposit_paid"
  | "progress_pending"
  | "final_pending"
  | "paid_in_full"
  | "overdue";

export function derivePaymentStatus(job: Job): DerivedPaymentStatus {
  const invoices = job.financials?.invoices ?? [];
  if (invoices.length === 0) return "no_invoice_plan";

  const hasOverdue = invoices.some(i => i.status === "overdue");
  if (hasOverdue) return "overdue";

  const deposit = invoices.find(i => i.type === "deposit");
  const final_ = invoices.find(i => i.type === "final");
  const progress = invoices.filter(i => i.type === "progress");

  const allPaid = invoices.every(i => i.status === "paid");
  if (final_ && allPaid) return "paid_in_full";
  if (final_ && !allPaid) return "final_pending";
  if (progress.some(i => i.status === "sent" || i.status === "draft")) return "progress_pending";
  if (deposit && deposit.status === "paid") return "deposit_paid";
  if (deposit && deposit.status !== "paid") return "deposit_pending";

  return "no_invoice_plan";
}

// ── Invoice Plan Generator ──

export function generateInvoicePlan(job: Job): InvoicePlan {
  if (job.financials?.paymentPlan) {
    const plan = job.financials.paymentPlan;
    const totalFromPlan = plan.lineItems.reduce((sum, li) => sum + Number(li.amount || 0), 0);
    const depositFromPlan = plan.lineItems
      .filter((li) => li.kind === "deposit")
      .reduce((sum, li) => sum + Number(li.amount || 0), 0);
    const totalContractValue = totalFromPlan > 0 ? totalFromPlan : getJobDisplayValue(job);
    return {
      depositAmount: Math.max(0, Math.round(depositFromPlan)),
      finalAmount: Math.max(0, Math.round(totalContractValue - depositFromPlan)),
      changeOrdersTotal: 0,
      totalContractValue: Math.round(totalContractValue),
    };
  }

  const contractValue = getJobDisplayValue(job);
  const changeOrdersTotal = (job.changeOrders ?? []).reduce((sum, co) => sum + (co.changeAmount ?? 0), 0);
  const totalContractValue = contractValue + changeOrdersTotal;
  const contractPaymentMethod = String((job.contractDocument as { paymentMethod?: unknown } | undefined)?.paymentMethod ?? "").toLowerCase();
  const isFinancing = contractPaymentMethod.includes("financ");
  const depositAmount = isFinancing ? 0 : Math.round(totalContractValue * 0.5); // financed jobs default to 0 down
  const finalAmount = totalContractValue - depositAmount;

  return { depositAmount, finalAmount, changeOrdersTotal, totalContractValue };
}

// ── Idempotent Invoice Generators ──

function makeInvoiceId(jobId: string, type: InvoiceType, seq?: number): string {
  return `INV-${jobId}-${type}${seq != null ? `-${seq}` : ""}`;
}

export function generateDepositInvoice(job: Job, explicitPlan?: InvoicePlan): Invoice | null {
  const existing = (job.financials?.invoices ?? []).find(i => i.type === "deposit");
  if (existing) return null; // idempotent — already exists

  const plan = explicitPlan ?? job.financials?.invoicePlan ?? generateInvoicePlan(job);
  if (!Number.isFinite(plan.depositAmount) || plan.depositAmount <= 0) return null;
  const now = new Date().toISOString();

  return {
    id: makeInvoiceId(job.id, "deposit"),
    type: "deposit",
    amount: plan.depositAmount,
    status: "draft",
    issuedAt: now,
    dueDate: addDays(now, 7),
  };
}

export function generateFinalInvoice(job: Job, explicitPlan?: InvoicePlan): Invoice | null {
  const existing = (job.financials?.invoices ?? []).find(i => i.type === "final");
  if (existing) return null;

  const plan = explicitPlan ?? job.financials?.invoicePlan ?? generateInvoicePlan(job);
  if (!Number.isFinite(plan.finalAmount) || plan.finalAmount <= 0) {
    return null;
  }
  const now = new Date().toISOString();

  return {
    id: makeInvoiceId(job.id, "final"),
    type: "final",
    amount: plan.finalAmount,
    status: "draft",
    issuedAt: now,
    dueDate: addDays(now, 14),
  };
}

export function generateProgressInvoice(job: Job, amount: number, milestone?: string): Invoice | null {
  const existing = job.financials?.invoices ?? [];
  const progressCount = existing.filter(i => i.type === "progress").length;
  const now = new Date().toISOString();

  return {
    id: makeInvoiceId(job.id, "progress", progressCount + 1),
    type: "progress",
    amount,
    status: "draft",
    issuedAt: now,
    dueDate: addDays(now, 14),
  };
}

// ── Payment Recording ──

export interface RecordPaymentResult {
  payment: Payment;
  updatedInvoice: Invoice;
  updatedPaymentStatus: DerivedPaymentStatus;
}

export function recordPayment(
  job: Job,
  invoiceId: string,
  amount: number,
  method: PaymentMethod
): RecordPaymentResult | null {
  const invoices = job.financials?.invoices ?? [];
  const invoice = invoices.find(i => i.id === invoiceId);
  if (!invoice) return null;

  const payment: Payment = {
    id: `PAY-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    invoiceId,
    amount,
    method,
    timestamp: new Date().toISOString(),
  };

  const updatedInvoice: Invoice = {
    ...invoice,
    status: "paid" as InvoiceStatus,
    paidAt: payment.timestamp,
    paymentMethod: method,
  };

  // Simulate updated job to derive new status
  const updatedInvoices = invoices.map(i => i.id === invoiceId ? updatedInvoice : i);
  const mockJob = { ...job, financials: { ...job.financials!, invoices: updatedInvoices, payments: [...(job.financials?.payments ?? []), payment] } };

  return {
    payment,
    updatedInvoice,
    updatedPaymentStatus: derivePaymentStatus(mockJob),
  };
}

// ── Readiness-Driven Payment Gating (Phase 2: Financial Clearance) ──

export interface PaymentGateResult {
  isPaymentBlocked: boolean;
  reason?: string;
  shouldAutoGenerateFinal: boolean;
  /** Phase 2: full clearance status */
  clearanceStatus?: "blocked" | "pending" | "cleared";
}

export function evaluatePaymentGate(job: Job): PaymentGateResult {
  // Inline pre_schedule clearance check to avoid circular deps with financialClearance.ts
  const plan = job.financials?.paymentPlan;
  const financing = job.financials?.financing;

  let isClearanceBlocked = false;

  if (plan) {
    // Helper: resolve effective dueAt
    const dueAt = (li: { dueAt?: string; kind: string }) =>
      li.dueAt ?? (li.kind === "deposit" ? "pre_schedule" : li.kind === "progress" ? "pre_install" : "post_install");
    const isMet = (li: { status: string }) => li.status === "paid" || li.status === "waived";

    // Deposit: only block if due pre_schedule
    if (plan.requiresDeposit) {
      const dep = plan.lineItems.find(li => li.kind === "deposit");
      if (dep && !isMet(dep) && dueAt(dep) === "pre_schedule") isClearanceBlocked = true;
    }

    // Financing: approval required before scheduling for any financed deal
    const hasFinancedItems = plan.lineItems.some(li => li.method === "financed");
    if (hasFinancedItems) {
      const fStatus = financing?.financingStatus ?? "not_applicable";
      if (fStatus === "declined" || fStatus === "not_applicable") isClearanceBlocked = true;
      // pending = soft concern, does not hard-block in gate
    }

    // Cash/card pre_schedule obligations
    const cashCardPreSched = plan.lineItems.filter(
      li => (li.method === "cash" || li.method === "card") && li.kind !== "deposit" && dueAt(li) === "pre_schedule"
    );
    if (cashCardPreSched.length > 0 && cashCardPreSched.some(li => !isMet(li))) {
      isClearanceBlocked = true;
    }
  } else {
    const contractPaymentMethod = String((job.contractDocument as { paymentMethod?: unknown } | undefined)?.paymentMethod ?? "").toLowerCase();
    const legacyFinancing = contractPaymentMethod.includes("financ");
    if (legacyFinancing) {
      const financingStatus = String(job.financials?.financing?.financingStatus ?? "pending").toLowerCase();
      isClearanceBlocked = financingStatus !== "approved";
    } else {
    // Legacy: deposit-only check
    const readiness = deriveReadinessFromJobExcludingBuildReview(job);
    const paymentStatus = derivePaymentStatus(job);
    const depositNotPaid = paymentStatus === "deposit_pending" || paymentStatus === "no_invoice_plan";
    const financialNotCleared = readiness.financial.state !== "deposit_received" && readiness.financial.state !== "paid" && readiness.financial.state !== "paid_in_full";
    isClearanceBlocked = depositNotPaid && financialNotCleared;
    }
  }

  const isMovingToScheduling = ["Ready for Scheduling", "Scheduled Tentative", "Scheduled Confirmed"].includes(job.currentStage);
  const isPaymentBlocked = isMovingToScheduling && isClearanceBlocked;

  // Gate 2: auto-generate final invoice on completion
  const isCompleted = job.currentStage === "Completed" || job.currentStage === "QA Review";
  const hasFinalInvoice = (job.financials?.invoices ?? []).some(i => i.type === "final");
  const shouldAutoGenerateFinal = isCompleted && !hasFinalInvoice;

  const clearanceStatus = isClearanceBlocked ? "blocked" as const : "cleared" as const;

  return {
    isPaymentBlocked,
    reason: isPaymentBlocked ? "Financial clearance required before scheduling" : undefined,
    shouldAutoGenerateFinal,
    clearanceStatus,
  };
}

// ── Financial Summary for Display ──

export interface FinancialSummary {
  contractValue: number;
  totalInvoiced: number;
  totalPaid: number;
  outstandingBalance: number;
  invoiceCount: number;
  paymentCount: number;
  paymentStatus: DerivedPaymentStatus;
}

export function getFinancialSummary(job: Job): FinancialSummary {
  const contractValue = getJobDisplayValue(job);
  const invoices = job.financials?.invoices ?? [];
  const payments = job.financials?.payments ?? [];

  const totalInvoiced = invoices
    .filter(i => i.status !== "draft")
    .reduce((s, i) => s + i.amount, 0);
  const paidFromInvoices = invoices
    .filter(i => i.status === "paid")
    .reduce((s, i) => s + i.amount, 0);
  const paidFromPayments = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalPaid = Math.max(0, payments.length > 0 ? paidFromPayments : paidFromInvoices);

  return {
    contractValue,
    totalInvoiced,
    totalPaid,
    outstandingBalance: Math.max(0, contractValue - totalPaid),
    invoiceCount: invoices.length,
    paymentCount: payments.length,
    paymentStatus: derivePaymentStatus(job),
  };
}

// ── Audit Events ──

export type InvoiceAuditAction =
  | "invoice_created"
  | "invoice_sent"
  | "payment_recorded"
  | "refund_recorded"
  | "invoice_overdue"
  | "plan_generated"
  | "invoice_voided";

export interface InvoiceAuditEntry {
  id: string;
  action: InvoiceAuditAction;
  timestamp: string;
  invoiceId?: string;
  paymentId?: string;
  detail: string;
}

export function createAuditEntry(action: InvoiceAuditAction, detail: string, ids?: { invoiceId?: string; paymentId?: string }): InvoiceAuditEntry {
  return {
    id: crypto.randomUUID(),
    action,
    timestamp: new Date().toISOString(),
    invoiceId: ids?.invoiceId,
    paymentId: ids?.paymentId,
    detail,
  };
}

// ── Helpers ──

function addDays(isoDate: string, days: number): string {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}
